import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@ui/accordion.tsx';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage
} from '@ui/form.tsx';
import { Input } from '@ui/input.tsx';
import { Button } from '@ui/button.tsx';
import { useNavigate } from '@tanstack/react-router';
import { AccordionTriggerLabel } from '../../components/workspace/accordion-trigger-label.tsx';
import { FileUploader } from '@r/components/import/file-uploader.tsx';
import { useWorkspaceFormStore } from '@admin/context/workspace-form-context.tsx';
import { useUploadFile } from '@r/hooks/use-upload-file.ts';
import { useEffect, useMemo, useRef } from 'react';
import { useApiMediasIdGet, useCreateWorkspace } from '@r/api/generated/client.ts';
import { useAllConfigs, findConfig, getConfigsFromResponse } from '@r/hooks/use-all-configs.ts';
import type { UploadedFile } from '@r/types/index.ts';
import type { ConfigConfigReadModel } from '@r/api/generated/client.schemas.ts';
import { toast } from 'sonner';
import { useUser } from '@r/api/auth.tsx';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { useTranslation } from 'react-i18next';
import { getTypeFromExtension, extractFilename } from '@r/utils/utils.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';

export const CreateWorkspaceStep1 = () => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const { updateFormData, workspaces } = useWorkspaceFormStore();
  const user = useUser();
  const activeWorkspace = user.data && getActiveWorkspaceUniversal(user.data.visibleWorkspaces);
  const activeWorkspaceId = activeWorkspace?.workspace.id as number;
  const formData = workspaces[activeWorkspaceId];

  const formSchemaStep1 = z.object({
    name: z
      .string()
      .min(2, t('form.workspace.name.too.short'))
      .max(50, t('form.workspace.name.too.long'))
      .describe(t('form.workspace.step1.schema.describe.name')),
    domain: z
      .string()
      .regex(
        /^((?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9])$/,
        t('form.workspace.invalid.domain')
      )
      .describe(t('form.workspace.step1.schema.describe.domain')),
    logo: z.union([
      z
        .string()
        .min(1), // Accepte les IRI de médias existants ou base64
      z.array(z.instanceof(File)).min(1, t('form.workspace.empty.logo'))
    ]),
    defaultEmailSender: z
      .union([z.string().email(t('form.workspace.invalid.email')), z.literal('')])
      .optional(),
    defaultHelpRecipient: z
      .union([z.string().email(t('form.workspace.invalid.email')), z.literal('')])
      .optional()
  });

  type FormSchema = z.infer<typeof formSchemaStep1>;

  // Fetch de toutes les configs disponibles (mutualisé avec les autres steps)
  const configsQuery = useAllConfigs();

  // Afficher une erreur si les configs ne peuvent pas être chargées
  useEffect(() => {
    if (configsQuery.isError && !configsQuery.isPending) {
      const errorMessage =
        configsQuery.error instanceof Error
          ? configsQuery.error.message
          : t('form.workspace.configs.load.error');
      toast.error(errorMessage);
    }
  }, [configsQuery.isError, configsQuery.isPending, configsQuery.error, t]);

  // Récupérer les configs : l'API peut retourner { data: [] } ou directement tableau vide
  const configs = getConfigsFromResponse(configsQuery.data);
  const hasConfigsWithIds = configs && configs.length > 0 && configs.some((c) => c.id);

  // Trouver la config logo pour récupérer le logo existant
  const logoConfig = findConfig(configs, 'media', 'logo');
  const logoConfigIri = logoConfig?.id ? `/configs/${logoConfig.id}` : null;

  // Récupérer le logo existant (nouveau format uniquement)
  const existingLogoConfig = formData?.workspaceConfigs?.find((config) => {
    return config.config === logoConfigIri;
  });

  const existingLogoId = existingLogoConfig?.media?.split('/').pop();
  const hasExistingLogoId = Boolean(existingLogoId);
  const logoQuery = useApiMediasIdGet(hasExistingLogoId ? String(existingLogoId) : undefined, {
    query: { enabled: hasExistingLogoId }
  });
  const existingLogoUploadedFile: UploadedFile | null = (() => {
    const media = logoQuery.data;
    if (!media) return null;
    const pathUri = media.pathUri ?? '';
    const filename = extractFilename(pathUri);
    return {
      iri: media.iri,
      pathUri,
      type: getTypeFromExtension(filename),
      name: filename
    };
  })();

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchemaStep1),
    defaultValues: {
      name: formData?.name ?? '',
      domain: formData?.domain ?? '',
      logo: [] as File[],
      defaultEmailSender: formData?.defaultEmailSender ?? '',
      defaultHelpRecipient: formData?.defaultHelpRecipient ?? ''
    },
    mode: 'onChange'
  });

  const defaultUploadedFiles = useMemo(
    () => (existingLogoUploadedFile ? [existingLogoUploadedFile] : []),
    [existingLogoUploadedFile?.iri]
  );

  const { onUpload, progresses, uploadedFiles, isUploading, setUploadedFiles } = useUploadFile({
    defaultUploadedFiles
  });

  const logoPreloadedRef = useRef(false);

  useEffect(() => {
    if (!existingLogoUploadedFile) return;
    if (logoPreloadedRef.current) return;

    const alreadySet =
      uploadedFiles?.some((file) => file.iri && file.iri === existingLogoUploadedFile.iri) ?? false;
    if (alreadySet) {
      logoPreloadedRef.current = true;
      return;
    }

    setUploadedFiles([existingLogoUploadedFile]);
    form.unregister('logo');
    form.setValue('logo', existingLogoUploadedFile.iri || '', { shouldValidate: true });
    logoPreloadedRef.current = true;
  }, [existingLogoUploadedFile, form, setUploadedFiles, uploadedFiles]);

  const navigate = useNavigate();

  const submitMutation = useCreateWorkspace({
    mutation: {
      onSuccess: async () => {
        await navigate({ to: '/create-workspace/step/2' });
      },
      onError: (error: unknown) => {
        if (isOrvalErrorFromPath(error, 'post', '/medias')) {
          showOrvalErrorToast(error, t('form.workspace.unknown.error'));
          return;
        }

        if (isOrvalErrorFromPath(error, 'put', '/admin/workspaces')) {
          showOrvalErrorToast(error, t('form.workspace.unknown.error'));
          return;
        }

        if (error instanceof Error && !('response' in error)) {
          toast.error(error.message);
          return;
        }

        toast.error(t('form.workspace.unknown.error'));
      }
    }
  });

  async function performSubmit(values: FormSchema) {
    const configs = getConfigsFromResponse(configsQuery.data);

    if (!configs || configs.length === 0) {
      toast.error(t('form.workspace.configs.not.loaded'));
      return;
    }

    // Vérifier que les configs ont bien des IDs
    const configsWithoutIds = configs.filter((c: ConfigConfigReadModel) => !c.id);
    if (configsWithoutIds.length > 0) {
      toast.error(t('form.workspace.configs.missing.ids'));
      return;
    }

    let logo: UploadedFile | undefined;

    if (existingLogoUploadedFile) {
      logo = existingLogoUploadedFile;
    } else if (Array.isArray(values.logo) && values.logo.length > 0) {
      const uploadedFiles = await onUpload(values.logo as File[]);
      logo = uploadedFiles?.[0] as UploadedFile;
    } else if (typeof values.logo === 'string' && values.logo.length > 0) {
      // Si c'est une string (IRI existant), on utilise existingLogoUploadedFile qui devrait être défini
      if (!existingLogoUploadedFile) {
        toast.error(t('form.workspace.empty.logo'));
        return;
      }
      logo = existingLogoUploadedFile;
    }

    if (!logo || !logo.iri) {
      toast.error(t('form.workspace.empty.logo'));
      return;
    }

    // Trouver les configs correspondantes
    const logoConfigFound = findConfig(configs, 'media', 'logo');
    const titleConfigFound = findConfig(configs, 'core', 'title');
    const isReferenceEnabledConfigFound = findConfig(configs, 'core', 'is-reference-enabled');

    if (!logoConfigFound?.id || !titleConfigFound?.id || !isReferenceEnabledConfigFound?.id) {
      toast.error(t('form.workspace.configs.not.found'));
      return;
    }

    // Construire les workspaceConfigs avec le nouveau format
    const workspaceConfigs = [
      {
        value: logo.name,
        media: logo.iri,
        config: `/configs/${logoConfigFound.id}`
      },
      {
        value: values.name,
        config: `/configs/${titleConfigFound.id}`
      },
      {
        value: 'false',
        config: `/configs/${isReferenceEnabledConfigFound.id}`
      }
    ];

    const newFormData = await updateFormData(activeWorkspaceId, {
      name: values.name,
      domain: values.domain,
      workspaceConfigs,
      defaultEmailSender: values.defaultEmailSender,
      defaultHelpRecipient: values.defaultHelpRecipient
    });
    try {
      await submitMutation.mutateAsync({
        id: String(activeWorkspaceId),
        data: newFormData as unknown as import('@r/api/generated/client.schemas.ts').WorkspaceWorkspaceWriteModel
      });
    } catch (_error: unknown) {
      // Mutation déjà gérée par onError, on évite toute propagation
      return;
    }
  }

  async function onSubmit(values: FormSchema) {
    // Vérifier la validité du formulaire avant de soumettre
    if (!form.formState.isValid) {
      // Déclencher la validation pour afficher les erreurs
      await form.trigger();
      return;
    }

    try {
      await performSubmit(values);
    } catch (error) {
      // Log l'erreur pour le débogage
      console.error('Error in onSubmit:', error);
      // erreurs déjà gérées dans performSubmit / onError
    }
  }

  // Si pas de logo à charger, on ne bloque pas le rendu
  // (si un logo existe et charge, il sera injecté au refresh du formulaire)

  return (
    <Form {...form} schema={formSchemaStep1}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-0.5">
        <Accordion type="single" collapsible className="w-full" defaultValue="item-1">
          <AccordionItem key="item-1" value="item-1" className="mb-4 border-b-0">
            <AccordionTrigger className="data-[state=open]:bg-gray-50 bg-white rounded-t-lg rounded-b-lg data-[state=open]:rounded-b-none p-4">
              <AccordionTriggerLabel
                fieldNames={['name']}
                label={t('form.workspace.name')}
                form={form}
              />
            </AccordionTrigger>
            <AccordionContent className="items-start p-4 bg-white rounded-b-lg">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormDescription>{t('form.workspace.name.description')}</FormDescription>
                    <FormLabel>{t('form.workspace.name')}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                )}
              />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem key="item-2" value="item-2" className="mb-4 border-b-0">
            <AccordionTrigger className="data-[state=open]:bg-gray-50 bg-white rounded-t-lg rounded-b-lg data-[state=open]:rounded-b-none p-4">
              <AccordionTriggerLabel
                fieldNames={['domain']}
                label={t('form.workspace.domain')}
                form={form}
              />
            </AccordionTrigger>
            <AccordionContent className="items-start p-4 bg-white rounded-b-lg">
              <FormField
                control={form.control}
                name="domain"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormDescription>{t('form.workspace.domain.description')}</FormDescription>
                    <FormLabel>{t('form.workspace.domain')}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem key="item-3" value="item-3" className="mb-4 border-b-0">
            <AccordionTrigger className="data-[state=open]:bg-gray-50 bg-white rounded-t-lg rounded-b-lg data-[state=open]:rounded-b-none p-4">
              <AccordionTriggerLabel
                fieldNames={['logo']}
                label={t('form.workspace.logo')}
                form={form}
                isFormValid={uploadedFiles.length > 0}
              />
            </AccordionTrigger>
            <AccordionContent className="items-start p-4 bg-white rounded-b-lg">
              <FormField
                control={form.control}
                name="logo"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormDescription>{t('form.workspace.logo.description')}</FormDescription>
                    <FormLabel>{t('form.workspace.logo')}</FormLabel>
                    <FileUploader
                      value={field.value as File[]}
                      onValueChange={field.onChange}
                      maxFiles={1}
                      maxSize={2 * 1024 * 1024}
                      cropAspect={[
                        { label: t('crop.shape.rectangle'), value: 3 },
                        { label: t('crop.shape.square'), value: 1 }
                      ]}
                      progresses={progresses}
                      disabled={isUploading}
                      uploadedFiles={uploadedFiles}
                      accept={{
                        'image/*': []
                      }}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="item-4" className="mb-4 border-b-0">
            <AccordionTrigger className="data-[state=open]:bg-gray-50 bg-white rounded-t-lg rounded-b-lg data-[state=open]:rounded-b-none p-4">
              <AccordionTriggerLabel
                fieldNames={['defaultEmailSender']}
                label={t('form.workspace.default.email.sender')}
                form={form}
              />
            </AccordionTrigger>
            <AccordionContent className="items-start p-4 bg-white rounded-b-lg">
              <div className="flex flex-row space-x-4 w-full">
                <FormField
                  control={form.control}
                  name="defaultEmailSender"
                  render={({ field }) => (
                    <FormItem className="flex-1 mb-4">
                      <FormDescription>
                        {t('form.workspace.default.email.sender.description')}
                      </FormDescription>
                      <FormLabel>{t('form.workspace.default.email.sender')}</FormLabel>
                      <FormControl>
                        <Input placeholder="mail@fiddy.app" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </AccordionContent>
          </AccordionItem>{' '}
          <AccordionItem value="item-5" className="mb-4 border-b-0">
            <AccordionTrigger className="data-[state=open]:bg-gray-50 bg-white rounded-t-lg rounded-b-lg data-[state=open]:rounded-b-none p-4">
              <AccordionTriggerLabel
                fieldNames={['defaultHelpRecipient']}
                label={t('form.workspace.default.email.help')}
                form={form}
              />
            </AccordionTrigger>
            <AccordionContent className="items-start p-4 bg-white rounded-b-lg">
              <div className="flex flex-row space-x-4 w-full">
                <FormField
                  control={form.control}
                  name="defaultHelpRecipient"
                  render={({ field }) => (
                    <FormItem className="flex-1 mb-4">
                      <FormDescription>
                        {t('form.workspace.default.email.help.description')}
                      </FormDescription>
                      <FormLabel>{t('form.workspace.default.email.help')}</FormLabel>
                      <FormControl>
                        <Input placeholder="mail@fiddy.app" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </AccordionContent>
          </AccordionItem>{' '}
        </Accordion>
        <Button
          rounded="full"
          type="submit"
          loading={form.formState.isSubmitting || submitMutation.isPending}
          disabled={
            !hasConfigsWithIds || configsQuery.isPending || configsQuery.isError || isUploading
          }
        >
          {t('form.workspace.next.step')}
        </Button>
      </form>
    </Form>
  );
};
